import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import {
  FaBars,
  FaTimes,
  FaShieldAlt,
  FaLock,
  FaUserShield,
  FaDatabase,
  FaChevronDown,
  FaArrowRight,
  FaHeadset,
  FaCheckCircle,
  FaEnvelope,
  FaPhoneAlt,
  FaFileAlt,
} from "react-icons/fa";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";

/* =========================================================
   BRAND
   SAME THEME AS REFUND POLICY
========================================================= */

const BRAND_BLUE = "#2563EB";
const BRAND_PURPLE = "#4F46E5";
const DARK = "#0B1220";
const TEXT = "#172033";
const MUTED = "#64748B";
const Header = styled.header`
  width: 100%;
  height: 76px;
  background: #ffffff;
  border-bottom: 1px solid #e8e8ef;
  position: sticky;
  top: 0;
  z-index: 1000;
`;

const HeaderInner = styled.div`
  width: 100%;
  max-width: 1380px;
  height: 100%;
  margin: 0 auto;
  padding: 0 42px;

  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 28px;

  @media (max-width: 900px) {
    padding: 0 24px;
  }
`;
const Navigation = styled.nav`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 27px;

  flex: 1;

  @media (max-width: 1100px) {
    gap: 17px;
  }

  @media (max-width: 900px) {
    display: none;
  }
`;

const NavLink = styled(Link)`
  color: #252b3a;
  text-decoration: none;
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  transition: color 0.2s ease;

  &:hover {
    color: #150080;
  }
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;

  flex-shrink: 0;
`;

const LoginLink = styled(Link)`
  color: #202635;
  text-decoration: none;

  font-size: 14px;
  font-weight: 700;

  padding: 10px 12px;

  &:hover {
    color: #150080;
  }
`;

const StartButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 10px;

  color: #ffffff;
  background: linear-gradient(
    135deg,
    ${BRAND_BLUE},
    ${BRAND_PURPLE}
  );

  text-decoration: none;

  padding: 11px 18px;
  border-radius: 6px;

  font-size: 13px;
  font-weight: 700;

  transition:
    background 0.2s ease,
    transform 0.2s ease;

  span {
    font-size: 17px;
    line-height: 1;
  }

  &:hover {
    background: #10005e;
    transform: translateY(-1px);
  }
`;

const LoginButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 10px;

  color: #ffffff;

  background: linear-gradient(
    135deg,
    #59047d 0%,
    #340548 55%,
    #59047d 100%
  );

  text-decoration: none;

  padding: 11px 18px;
  border-radius: 6px;

  font-size: 13px;
  font-weight: 700;

  box-shadow: 0 7px 18px rgba(21, 0, 128, 0.2);

  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    filter 0.2s ease;

  span {
    font-size: 17px;
    line-height: 1;
  }

  &:hover {
    transform: translateY(-1px);
    filter: brightness(1.08);
    box-shadow: 0 10px 24px rgba(21, 0, 128, 0.28);
  }
`;

/* =========================================================
   GLOBAL
========================================================= */

const Page = styled.div`
  min-height: 100vh;

  background:
    radial-gradient(
      circle at 80% 0%,
      rgba(79, 70, 229, 0.09),
      transparent 32%
    ),
    radial-gradient(
      circle at 10% 25%,
      rgba(37, 99, 235, 0.07),
      transparent 28%
    ),
    #f8fafc;

  color: ${TEXT};
`;

/* =========================================================
   NAVBAR
========================================================= */

const TopNav = styled.header`
  position: sticky;
  top: 0;
  z-index: 1000;

  background: rgba(255, 255, 255, 0.92);

  backdrop-filter: blur(16px);

  border-bottom: 1px solid #e5e7eb;
`;

const NavInner = styled.div`
  max-width: 1180px;

  height: 74px;

  margin: 0 auto;

  padding: 0 24px;

  display: flex;

  align-items: center;

  justify-content: space-between;
`;

const Brand = styled(Link)`
  display: flex;

  align-items: center;

  gap: 10px;

  text-decoration: none;

  color: ${DARK};
`;

const BrandMark = styled.div`
  width: 38px;
  height: 38px;

  border-radius: 10px;

  display: flex;

  align-items: center;

  justify-content: center;

  background: linear-gradient(
    135deg,
    ${BRAND_BLUE},
    ${BRAND_PURPLE}
  );

  color: white;

  font-size: 13px;

  font-weight: 800;

  box-shadow:
    0 7px 18px rgba(37, 99, 235, 0.2);
`;

const BrandText = styled.div`
  display: flex;

  flex-direction: column;

  line-height: 1;

  strong {
    font-size: 18px;

    font-weight: 800;

    letter-spacing: -0.4px;

    color: ${DARK};
  }

  span {
    margin-top: 4px;

    font-size: 9px;

    font-weight: 700;

    letter-spacing: 1.6px;

    color: ${BRAND_BLUE};
  }
`;

const NavLinks = styled.nav`
  display: flex;

  align-items: center;

  gap: 30px;

  a {
    position: relative;

    color: #475569;

    text-decoration: none;

    font-size: 14px;

    font-weight: 600;

    transition: 0.2s ease;

    &:hover {
      color: ${BRAND_BLUE};
    }
  }

  .active {
    color: ${BRAND_BLUE};
  }

  .active::after {
    content: "";

    position: absolute;

    left: 0;
    right: 0;

    bottom: -27px;

    height: 2px;

    border-radius: 2px;

    background: ${BRAND_BLUE};
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const NavButton = styled(Link)`
  display: flex;

  align-items: center;

  gap: 7px;

  padding: 10px 17px;

  border-radius: 8px;

  color: white !important;

  text-decoration: none;

  background: linear-gradient(
    135deg,
    ${BRAND_BLUE},
    ${BRAND_PURPLE}
  );

  font-size: 13px !important;

  font-weight: 700 !important;

  box-shadow:
    0 7px 16px rgba(37, 99, 235, 0.18);

  transition: 0.2s ease;

  &:hover {
    color: white !important;

    transform: translateY(-1px);
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const Burger = styled.button`
  display: none;

  border: 0;

  background: transparent;

  color: ${DARK};

  font-size: 21px;

  cursor: pointer;

  @media (max-width: 768px) {
    display: block;
  }
`;

/* =========================================================
   MOBILE MENU
========================================================= */

const MobileMenu = styled(motion.div)`
  position: fixed;

  inset: 0;

  z-index: 2000;

  background: white;

  padding: 90px 28px 30px;
`;

const Close = styled.button`
  position: absolute;

  top: 24px;

  right: 24px;

  border: 0;

  background: transparent;

  color: ${DARK};

  font-size: 23px;

  cursor: pointer;
`;

const MobileLinks = styled.div`
  display: flex;

  flex-direction: column;

  gap: 25px;

  a {
    color: ${DARK};

    text-decoration: none;

    font-size: 22px;

    font-weight: 700;

    transition: 0.2s ease;

    &:hover {
      color: ${BRAND_BLUE};
    }
  }
`;

const MobileCta = styled(Link)`
  margin-top: 10px;

  display: inline-flex;

  justify-content: center;

  align-items: center;

  gap: 8px;

  padding: 13px 18px;

  border-radius: 9px;

  background: linear-gradient(
    135deg,
    ${BRAND_BLUE},
    ${BRAND_PURPLE}
  );

  color: white !important;

  font-size: 15px !important;
`;

/* =========================================================
   HERO
========================================================= */

const Hero = styled.section`
  max-width: 1180px;

  margin: 0 auto;

  padding: 78px 24px 48px;

  display: grid;

  grid-template-columns: 1.25fr 0.75fr;

  align-items: center;

  gap: 70px;

  @media (max-width: 850px) {
    grid-template-columns: 1fr;

    padding-top: 58px;

    gap: 35px;
  }
`;

const HeroContent = styled.div`
  max-width: 700px;
`;

const Eyebrow = styled.div`
  display: inline-flex;

  align-items: center;

  gap: 8px;

  padding: 7px 12px;

  border-radius: 999px;

  background: #eff6ff;

  border: 1px solid #dbeafe;

  color: ${BRAND_BLUE};

  font-size: 11px;

  font-weight: 800;

  letter-spacing: 0.8px;

  text-transform: uppercase;
`;

const HeroTitle = styled.h1`
  margin: 18px 0 16px;

  color: ${DARK};

  font-size: clamp(38px, 5vw, 58px);

  line-height: 1.06;

  letter-spacing: -2.4px;

  font-weight: 800;

  span {
    background: linear-gradient(
      135deg,
      ${BRAND_BLUE},
      ${BRAND_PURPLE}
    );

    -webkit-background-clip: text;

    -webkit-text-fill-color: transparent;
  }
`;

const HeroText = styled.p`
  max-width: 650px;

  margin: 0;

  color: ${MUTED};

  font-size: 17px;

  line-height: 1.75;
`;

const HeroNote = styled.div`
  display: flex;

  align-items: center;

  gap: 9px;

  margin-top: 24px;

  color: #64748b;

  font-size: 12px;

  svg {
    color: ${BRAND_BLUE};
  }
`;

/* =========================================================
   HERO VISUAL
========================================================= */

const HeroVisual = styled.div`
  position: relative;

  min-height: 280px;

  display: flex;

  align-items: center;

  justify-content: center;
`;

const Glow = styled.div`
  position: absolute;

  width: 245px;

  height: 245px;

  border-radius: 50%;

  background: linear-gradient(
    135deg,
    rgba(37, 99, 235, 0.17),
    rgba(79, 70, 229, 0.12)
  );

  filter: blur(4px);
`;

const VisualCard = styled(motion.div)`
  position: relative;

  z-index: 2;

  width: 100%;

  max-width: 350px;

  padding: 27px;

  border: 1px solid #e2e8f0;

  border-radius: 18px;

  background: rgba(255, 255, 255, 0.92);

  box-shadow:
    0 24px 60px rgba(15, 23, 42, 0.1),
    0 3px 12px rgba(15, 23, 42, 0.04);
`;

const VisualHeader = styled.div`
  display: flex;

  align-items: center;

  gap: 13px;

  padding-bottom: 20px;

  border-bottom: 1px solid #edf2f7;
`;

const VisualIcon = styled.div`
  width: 44px;

  height: 44px;

  border-radius: 12px;

  display: flex;

  align-items: center;

  justify-content: center;

  color: white;

  background: linear-gradient(
    135deg,
    ${BRAND_BLUE},
    ${BRAND_PURPLE}
  );
`;

const VisualTitle = styled.div`
  strong {
    display: block;

    color: ${DARK};

    font-size: 15px;
  }

  span {
    display: block;

    margin-top: 4px;

    color: #94a3b8;

    font-size: 11px;
  }
`;

const VisualLine = styled.div`
  display: flex;

  align-items: center;

  gap: 9px;

  margin-top: 17px;

  color: #475569;

  font-family: monospace;

  font-size: 11px;

  span {
    color: ${BRAND_BLUE};

    font-weight: 700;
  }
`;

/* =========================================================
   POLICY SECTION
========================================================= */

const PolicySection = styled.section`
  max-width: 1180px;

  margin: 0 auto;

  padding: 22px 24px 85px;
`;

const SectionHeader = styled.div`
  margin-bottom: 25px;

  h2 {
    margin: 0;

    color: ${DARK};

    font-size: 27px;

    letter-spacing: -0.7px;
  }

  p {
    margin: 7px 0 0;

    color: ${MUTED};

    font-size: 14px;

    line-height: 1.6;
  }
`;

/* =========================================================
   POLICY GRID
========================================================= */

const PolicyGrid = styled.div`
  display: grid;

  grid-template-columns:
    repeat(2, minmax(0, 1fr));

  gap: 17px;

  @media (max-width: 800px) {
    grid-template-columns: 1fr;
  }
`;

/* =========================================================
   POLICY CARD
========================================================= */

const PolicyCard = styled(motion.div)`
  border: 1px solid #e2e8f0;

  border-radius: 13px;

  background: white;

  overflow: hidden;

  box-shadow:
    0 7px 24px rgba(15, 23, 42, 0.045);

  transition: 0.22s ease;

  &:hover {
    border-color: #c7d2fe;

    box-shadow:
      0 14px 32px rgba(37, 99, 235, 0.08);
  }
`;

const AccordionHeader = styled.button`
  width: 100%;

  padding: 19px 20px;

  display: flex;

  align-items: center;

  gap: 13px;

  border: 0;

  background: white;

  color: ${DARK};

  text-align: left;

  cursor: pointer;
`;

const Number = styled.div`
  width: 37px;

  height: 37px;

  flex-shrink: 0;

  display: flex;

  align-items: center;

  justify-content: center;

  border-radius: 9px;

  background: #eff6ff;

  color: ${BRAND_BLUE};

  font-size: 11px;

  font-weight: 800;
`;

const HeaderText = styled.div`
  flex: 1;

  strong {
    display: block;

    color: ${DARK};

    font-size: 14px;

    font-weight: 750;
  }

  span {
    display: block;

    margin-top: 4px;

    color: #94a3b8;

    font-size: 10px;

    font-weight: 600;

    text-transform: uppercase;

    letter-spacing: 0.7px;
  }
`;

const Chevron = styled(motion.div)`
  width: 32px;

  height: 32px;

  flex-shrink: 0;

  display: flex;

  align-items: center;

  justify-content: center;

  border-radius: 8px;

  background: #f8fafc;

  color: ${BRAND_BLUE};

  font-size: 12px;
`;

const AccordionContent = styled(motion.div)`
  overflow: hidden;
`;

const ContentInner = styled.div`
  padding: 0 20px 20px 70px;

  color: ${MUTED};

  font-size: 13.5px;

  line-height: 1.75;

  @media (max-width: 600px) {
    padding-left: 20px;
  }
`;

const PolicyText = styled.p`
  margin: 0 0 14px;

  color: ${MUTED};

  font-size: 13.5px;

  line-height: 1.75;

  &:last-child {
    margin-bottom: 0;
  }

  b,
  strong {
    color: ${DARK};
  }
`;

const Highlight = styled.span`
  color: ${BRAND_BLUE};

  font-weight: 700;
`;

/* =========================================================
   LIST
========================================================= */

const InfoList = styled.ul`
  margin: 7px 0 15px;

  padding-left: 21px;

  li {
    margin-bottom: 7px;

    padding-left: 3px;

    color: ${MUTED};

    font-size: 13.5px;

    line-height: 1.6;
  }

  li::marker {
    color: ${BRAND_BLUE};
  }
`;

/* =========================================================
   CONTACT BOX
========================================================= */

const ContactBox = styled.div`
  margin-top: 17px;

  padding: 16px 17px;

  border: 1px solid #e2e8f0;

  border-radius: 10px;

  background: #f8fafc;
`;

const ContactRow = styled.div`
  display: flex;

  align-items: center;

  gap: 12px;

  margin-bottom: 12px;

  color: ${MUTED};

  font-size: 13px;

  &:last-child {
    margin-bottom: 0;
  }

  svg {
    color: ${BRAND_BLUE};

    flex-shrink: 0;
  }

  a {
    color: ${BRAND_BLUE};

    text-decoration: none;

    font-weight: 600;

    &:hover {
      text-decoration: underline;
    }
  }

  @media (max-width: 600px) {
    align-items: flex-start;
  }
`;

const ContactLabel = styled.span`
  min-width: 60px;

  color: ${DARK};

  font-size: 12px;

  font-weight: 700;
`;

const Address = styled.div`
  display: flex;

  align-items: flex-start;

  gap: 12px;

  margin-top: 16px;

  color: ${MUTED};

  font-size: 13px;

  line-height: 1.7;

  svg {
    color: ${BRAND_BLUE};

    margin-top: 3px;

    flex-shrink: 0;
  }

  strong {
    color: ${DARK};
  }
`;

/* =========================================================
   READ ALL
========================================================= */

const ExpandButton = styled.button`
  width: 100%;

  margin-top: 22px;

  padding: 13px;

  display: flex;

  align-items: center;

  justify-content: center;

  gap: 8px;

  border: 1px solid #e2e8f0;

  border-radius: 10px;

  background: white;

  color: #64748b;

  font-size: 12px;

  font-weight: 700;

  cursor: pointer;

  transition: 0.2s ease;

  &:hover {
    border-color: #c7d2fe;

    background: #eff6ff;

    color: ${BRAND_BLUE};
  }
`;

/* =========================================================
   SUPPORT BANNER
========================================================= */

const SupportBanner = styled(motion.div)`
  margin-top: 22px;

  padding: 26px 28px;

  border-radius: 15px;

  background: linear-gradient(
    135deg,
    #0f172a,
    #172554
  );

  display: flex;

  align-items: center;

  justify-content: space-between;

  gap: 25px;

  color: white;

  @media (max-width: 700px) {
    flex-direction: column;

    align-items: flex-start;
  }
`;

const SupportContent = styled.div`
  display: flex;

  align-items: center;

  gap: 15px;
`;

const SupportIcon = styled.div`
  width: 43px;

  height: 43px;

  flex-shrink: 0;

  display: flex;

  align-items: center;

  justify-content: center;

  border-radius: 10px;

  background: rgba(255, 255, 255, 0.1);

  color: #93c5fd;
`;

const SupportText = styled.div`
  strong {
    display: block;

    font-size: 15px;
  }

  span {
    display: block;

    margin-top: 5px;

    color: #cbd5e1;

    font-size: 12px;

    line-height: 1.5;
  }

  a {
    display: inline-block;

    margin-top: 5px;

    color: #93c5fd;

    text-decoration: none;

    font-size: 12px;

    font-weight: 700;
  }
`;

const ContactButton = styled(Link)`
  display: inline-flex;

  align-items: center;

  gap: 8px;

  padding: 10px 15px;

  border-radius: 8px;

  background: white;

  color: ${DARK};

  text-decoration: none;

  font-size: 12px;

  font-weight: 700;

  white-space: nowrap;

  transition: 0.2s ease;

  &:hover {
    transform: translateY(-1px);

    color: ${BRAND_BLUE};
  }
`;

/* =========================================================
   FOOTER
========================================================= */

const Footer = styled.footer`
  border-top: 1px solid #e2e8f0;

  background: white;
`;

const FooterInner = styled.div`
  max-width: 1180px;

  margin: auto;

  padding: 25px 24px;

  display: flex;

  align-items: center;

  justify-content: space-between;

  gap: 20px;

  @media (max-width: 650px) {
    flex-direction: column;

    align-items: flex-start;
  }
`;

const FooterBrand = styled.div`
  color: #64748b;

  font-size: 12px;

  strong {
    color: ${DARK};
  }
`;

const FooterLinks = styled.div`
  display: flex;

  flex-wrap: wrap;

  gap: 20px;

  a {
    color: #64748b;

    text-decoration: none;

    font-size: 12px;

    font-weight: 600;

    &:hover {
      color: ${BRAND_BLUE};
    }
  }
`;

/* =========================================================
   WHATSAPP
========================================================= */

const WhatsAppButton = styled.a`
  position: fixed;

  right: 22px;

  bottom: 22px;

  width: 52px;

  height: 52px;

  display: flex;

  align-items: center;

  justify-content: center;

  border-radius: 50%;

  background: #25d366;

  color: white;

  font-size: 23px;

  text-decoration: none;

  z-index: 900;

  box-shadow:
    0 10px 25px rgba(37, 211, 102, 0.25);

  transition: 0.2s ease;

  &:hover {
    transform: translateY(-3px) scale(1.04);
  }

  @media (max-width: 600px) {
    right: 16px;

    bottom: 16px;
  }
`;

/* =========================================================
   PRIVACY POLICY DATA
   EXISTING CONTENT RETAINED
========================================================= */

const SECTIONS = [
  {
    title: "Information We Collect",
    subtitle:
      "Personal information collected during registration and eKYC",
    icon: <FaDatabase />,

    content: (
      <>
        <PolicyText>
          During registration and the electronic Know Your Customer
          (eKYC) process, we collect certain personal information
          required to verify your identity and comply with applicable
          legal requirements.
        </PolicyText>

        <InfoList>
          <li>Full Name</li>
          <li>Email Address</li>
          <li>Mobile Number</li>
          <li>IP Address</li>
          <li>Aadhaar Number</li>
          <li>PAN Number</li>
          <li>Address</li>
          <li>GST Number (if applicable)</li>
        </InfoList>

        <PolicyText>
          This data is collected solely for legal compliance purposes
          and to verify your identity.
        </PolicyText>
      </>
    ),
  },

  {
    title: "How We Collect Information",
    subtitle:
      "When and how personal data is collected",
    icon: <FaUserShield />,

    content: (
      <>
        <PolicyText>
          We collect personal data:
        </PolicyText>

        <InfoList>
          <li>When you register on our website</li>

          <li>
            During the electronic Know Your Customer (eKYC) process
          </li>

          <li>
            Automatically through IP tracking
          </li>
        </InfoList>

        <PolicyText>
          All data is collected with your knowledge and consent.
        </PolicyText>
      </>
    ),
  },

  {
    title: "Use of Your Information",
    subtitle:
      "How your personal information is used",
    icon: <FaShieldAlt />,

    content: (
      <>
        <PolicyText>
          Your personal information is used strictly for:
        </PolicyText>

        <InfoList>
          <li>Identity verification</li>

          <li>
            Compliance with applicable legal and regulatory
            requirements
          </li>

          <li>
            Ensuring the security and integrity of our platform
          </li>
        </InfoList>

        <PolicyText>
          We do <b>not</b> use your information for marketing or
          advertising without your explicit consent.
        </PolicyText>
      </>
    ),
  },

  {
    title: "Data Sharing and Disclosure",
    subtitle:
      "How we handle disclosure of your personal data",
    icon: <FaLock />,

    content: (
      <>
        <PolicyText>
          We <b>do not share, sell, rent, or disclose</b> your
          personal data to any third parties.
        </PolicyText>

        <PolicyText>
          In exceptional cases, we may disclose your data if
          required to do so by law or in response to a valid legal
          request by a governmental authority.
        </PolicyText>
      </>
    ),
  },

  {
    title: "Your Data Rights",
    subtitle:
      "Your rights regarding your personal information",
    icon: <FaUserShield />,

    content: (
      <>
        <PolicyText>
          You have the right to:
        </PolicyText>

        <InfoList>
          <li>
            Access the personal data we hold about you
          </li>

          <li>
            Request correction of any incorrect information
          </li>

          <li>
            Request deletion of your data
          </li>
        </InfoList>

        <PolicyText>
          To exercise these rights, please contact our support
          team:
        </PolicyText>

        <ContactBox>
          <ContactRow>
            <FaEnvelope />

            <ContactLabel>
              Email
            </ContactLabel>

            <a href="mailto:info.apiexch@gmail.com">
              info.apiexch@gmail.com
            </a>
          </ContactRow>

          <ContactRow>
            <FaPhoneAlt />

            <ContactLabel>
              Phone
            </ContactLabel>

            <a href="tel:+919689299292">
              +91 96892 99292
            </a>
          </ContactRow>
        </ContactBox>
      </>
    ),
  },

  {
    title: "Data Security",
    subtitle:
      "Measures taken to protect your information",
    icon: <FaLock />,

    content: (
      <>
        <PolicyText>
          We take appropriate security measures to protect your
          data from unauthorized access, alteration, disclosure,
          or destruction.
        </PolicyText>

        <PolicyText>
          We follow the guidelines of the IT Rules, 2011 for
          handling Sensitive Personal Data or Information (SPDI).
        </PolicyText>
      </>
    ),
  },

  {
    title: "Children's Privacy",
    subtitle:
      "Protection of information relating to children",
    icon: <FaUserShield />,

    content: (
      <PolicyText>
        Our website is{" "}
        <b>not intended for children under the age of 13</b>.
        We do not knowingly collect personal information from
        minors.
      </PolicyText>
    ),
  },

  {
    title: "Grievance Redressal",
    subtitle:
      "How to contact our Grievance Officer",
    icon: <FaHeadset />,

    content: (
      <>
        <PolicyText>
          In case of any grievances regarding your personal data,
          you may reach out to our Grievance Officer using the
          details below:
        </PolicyText>

        <ContactBox>
          <ContactRow>
            <FaUserShield />

            <ContactLabel>
              Name
            </ContactLabel>

            <span>
              Grievance Officer
            </span>
          </ContactRow>

          <ContactRow>
            <FaEnvelope />

            <ContactLabel>
              Email
            </ContactLabel>

            <a href="mailto:info.apiexch@gmail.com">
              info.apiexch@gmail.com
            </a>
          </ContactRow>

          <ContactRow>
            <FaPhoneAlt />

            <ContactLabel>
              Phone
            </ContactLabel>

            <a href="tel:+919689299292">
              +91 96892 99292
            </a>
          </ContactRow>

          <Address>
            <FaShieldAlt />

            <div>
              <ContactLabel>
                Address
              </ContactLabel>

              <br />

              <strong>
                Erech ReSellers Private Limited
              </strong>

              <br />

              H No 93, Khuntefal (P), Kada (Beed),

              <br />

              Beed, Ashti, Maharashtra 414202,

              <br />

              India.
            </div>
          </Address>
        </ContactBox>
      </>
    ),
  },

  {
    title: "Changes to This Policy",
    subtitle:
      "Updates to our Privacy Policy",
    icon: <FaCheckCircle />,

    content: (
      <>
        <PolicyText>
          We may update this Privacy Policy from time to time to
          reflect changes in legal, regulatory, or operational
          requirements.
        </PolicyText>

        <PolicyText>
          We encourage you to review this page periodically for
          any updates.
        </PolicyText>
      </>
    ),
  },
];

/* =========================================================
   ACCORDION ITEM
========================================================= */

const AccordionItem = ({
  section,
  index,
  open,
  onToggle,
}) => {
  return (
    <PolicyCard
      initial={{
        opacity: 0,
        y: 15,
      }}
      whileInView={{
        opacity: 1,
        y: 0,
      }}
      viewport={{
        once: true,
        amount: 0.15,
      }}
      transition={{
        duration: 0.35,
      }}
    >
      <AccordionHeader
        type="button"
        onClick={onToggle}
        aria-expanded={open}
      >
        <Number>
          {String(index + 1).padStart(2, "0")}
        </Number>

        <HeaderText>
          <strong>
            {section.title}
          </strong>

          <span>
            Privacy Policy
          </span>
        </HeaderText>

        <Chevron
          animate={{
            rotate: open ? 180 : 0,
          }}
          transition={{
            duration: 0.22,
          }}
        >
          <FaChevronDown />
        </Chevron>
      </AccordionHeader>

      <AnimatePresence initial={false}>
        {open && (
          <AccordionContent
            initial={{
              height: 0,
              opacity: 0,
            }}
            animate={{
              height: "auto",
              opacity: 1,
            }}
            exit={{
              height: 0,
              opacity: 0,
            }}
            transition={{
              duration: 0.28,
              ease: "easeOut",
            }}
          >
            <ContentInner>
              {section.content}
            </ContentInner>
          </AccordionContent>
        )}
      </AnimatePresence>
    </PolicyCard>
  );
};

/* =========================================================
   PAGE
========================================================= */

const PrivacyPolicy = () => {
  const [menuOpen, setMenuOpen] =
    useState(false);

  const [openIndex, setOpenIndex] =
    useState(null);

  const [meta, setMeta] =
    useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);

    document.documentElement.scrollTop = 0;

    document.body.scrollTop = 0;

    axios
      .get(
        "https://apis.apiexchange.co.in/v1/meta"
      )
      .then((res) => {
        setMeta(
          res?.data?.data || null
        );
      })
      .catch(() => {
        setMeta(null);
      });
  }, []);

  const toggleSection = (index) => {
    setOpenIndex((current) =>
      current === index ? null : index
    );
  };

  const toggleAll = () => {
    setOpenIndex((current) =>
      current === "all" ? null : "all"
    );
  };

  const isOpen = (index) =>
    openIndex === "all" ||
    openIndex === index;

  return (
    <Page>
      {/* =====================================================
          NAVIGATION
      ===================================================== */}

      <Header>
        <HeaderInner>
          {/* <LogoWrap>
            <Logo
              src={logo}
              alt="Recharge Junction Logo"
              onError={(e) => {
                e.currentTarget.src = defaultLogo;
              }}
            />
          </LogoWrap> */}
          <Brand to="/">
            <BrandMark>API</BrandMark>

            <BrandText>
              <strong>API EXCHANGE</strong>
              <span>POWERING BUSINESS</span>
            </BrandText>
          </Brand>

          <Navigation>
            {/* <NavLink to="/services">Services</NavLink> */}
            <NavLink to="/privacy-policy">Privacy Policy</NavLink>
            <NavLink to="/terms-and-conditions">
              Terms & Conditions
            </NavLink>
            <NavLink to="/refund-policy">
              Refund & Cancellation
            </NavLink>
            <NavLink to="/contact">Contact Us</NavLink>
          </Navigation>

          <HeaderActions>
            <LoginButton to="/login">
              Login
              <span>→</span>
            </LoginButton>

            <StartButton to="/register">
              Start Free
              <span>→</span>
            </StartButton>
          </HeaderActions>
        </HeaderInner>
      </Header>

      {/* =====================================================
          MOBILE MENU
      ===================================================== */}

      <AnimatePresence>
        {menuOpen && (
          <MobileMenu
            initial={{
              opacity: 0,
              x: "100%",
            }}
            animate={{
              opacity: 1,
              x: 0,
            }}
            exit={{
              opacity: 0,
              x: "100%",
            }}
            transition={{
              duration: 0.22,
            }}
          >
            <Close
              type="button"
              aria-label="Close menu"
              onClick={() =>
                setMenuOpen(false)
              }
            >
              <FaTimes />
            </Close>

            <MobileLinks>
              <Link
                to="/"
                onClick={() =>
                  setMenuOpen(false)
                }
              >
                Home
              </Link>

              <Link
                to="/login"
                onClick={() =>
                  setMenuOpen(false)
                }
              >
                Login
              </Link>

              <Link
                to="/register"
                onClick={() =>
                  setMenuOpen(false)
                }
              >
                Register
              </Link>

              <Link
                to="/contact"
                onClick={() =>
                  setMenuOpen(false)
                }
              >
                Contact
              </Link>

              <Link
                to="/privacy-policy"
                onClick={() =>
                  setMenuOpen(false)
                }
              >
                Privacy Policy
              </Link>

              <MobileCta
                to="/register"
                onClick={() =>
                  setMenuOpen(false)
                }
              >
                Start Free

                <FaArrowRight />
              </MobileCta>
            </MobileLinks>
          </MobileMenu>
        )}
      </AnimatePresence>

      {/* =====================================================
          HERO
      ===================================================== */}

      <Hero>
        <HeroContent>
          <Eyebrow>
            <FaShieldAlt />

            Privacy & Data Protection
          </Eyebrow>

          <HeroTitle>
            Your privacy.
            <br />

            <span>
              Our responsibility.
            </span>
          </HeroTitle>

          <HeroText>
            This Privacy Policy explains how API Exchange
            collects, uses, protects and handles personal
            information when you use our website, platform
            and services.
          </HeroText>

          <HeroNote>
            <FaFileAlt />

            Effective Date:{" "}
            <strong>
              May 1, 2025
            </strong>
          </HeroNote>
        </HeroContent>

        <HeroVisual>
          <Glow />

          <VisualCard
            initial={{
              opacity: 0,
              y: 15,
            }}
            animate={{
              opacity: 1,
              y: 0,
            }}
            transition={{
              duration: 0.5,
            }}
          >
            <VisualHeader>
              <VisualIcon>
                <FaShieldAlt />
              </VisualIcon>

              <VisualTitle>
                <strong>
                  Privacy Policy
                </strong>

                <span>
                  API Exchange · Data Protection
                </span>
              </VisualTitle>
            </VisualHeader>

            <VisualLine>
              <span>DATA</span>
              Protected
            </VisualLine>

            <VisualLine>
              <span>ACCESS</span>
              Controlled
            </VisualLine>

            <VisualLine>
              <span>SECURITY</span>
              Enabled
            </VisualLine>
          </VisualCard>
        </HeroVisual>
      </Hero>

      {/* =====================================================
          POLICY CONTENT
      ===================================================== */}

      <PolicySection>
        <SectionHeader>
          <h2>
            Privacy & data protection terms
          </h2>

          <p>
            The following sections explain what information
            we collect, how it is used, how it is protected
            and the rights available to you.
          </p>
        </SectionHeader>

        <PolicyGrid>
          {SECTIONS.map(
            (section, index) => (
              <AccordionItem
                key={index}
                section={section}
                index={index}
                open={isOpen(index)}
                onToggle={() =>
                  toggleSection(index)
                }
              />
            )
          )}
        </PolicyGrid>

        {/* ===================================================
            READ ALL
        =================================================== */}

        <ExpandButton
          type="button"
          onClick={toggleAll}
        >
          {openIndex === "all"
            ? "Collapse All Sections"
            : "Read All Sections"}

          <FaChevronDown
            style={{
              transform:
                openIndex === "all"
                  ? "rotate(180deg)"
                  : "rotate(0deg)",

              transition:
                "transform 0.2s ease",
            }}
          />
        </ExpandButton>

        {/* ===================================================
            SUPPORT BANNER
        =================================================== */}

        <SupportBanner
          initial={{
            opacity: 0,
            y: 15,
          }}
          whileInView={{
            opacity: 1,
            y: 0,
          }}
          viewport={{
            once: true,
          }}
          transition={{
            duration: 0.4,
          }}
        >
          <SupportContent>
            <SupportIcon>
              <FaHeadset />
            </SupportIcon>

            <SupportText>
              <strong>
                Have a privacy-related question?
              </strong>

              <span>
                Contact our team if you need clarification
                or assistance regarding your personal
                information.
              </span>

              <a href="mailto:info.apiexch@gmail.com">
                info.apiexch@gmail.com
              </a>
            </SupportText>
          </SupportContent>

          <ContactButton to="/contact">
            Contact Us

            <FaArrowRight />
          </ContactButton>
        </SupportBanner>
      </PolicySection>

      {/* =====================================================
          FOOTER
      ===================================================== */}
      {/* 
      <Footer>
        <FooterInner>
          <FooterBrand>
            ©{" "}
            {new Date().getFullYear()}{" "}
            <strong>
              {meta?.firmName ||
                "API Exchange"}
            </strong>
            . All rights reserved.
          </FooterBrand>

          <FooterLinks>
            <Link to="/">
              Home
            </Link>

            <Link to="/login">
              Login
            </Link>

            <Link to="/register">
              Register
            </Link>

            <Link to="/contact">
              Contact
            </Link>

            <Link to="/privacy-policy">
              Privacy Policy
            </Link>
          </FooterLinks>
        </FooterInner>
      </Footer> */}

      {/* =====================================================
          FLOATING WHATSAPP
      ===================================================== */}

      <WhatsAppButton
        href="https://wa.me/"
        target="_blank"
        rel="noopener noreferrer"
        aria-label="Chat on WhatsApp"
      >
        <span
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {/* Keep your existing WhatsApp icon if imported */}
          <span style={{ fontSize: "20px" }}>
            WhatsApp
          </span>
        </span>
      </WhatsAppButton>
    </Page>
  );
};

export default PrivacyPolicy;